/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useMemo } from 'react';
import { useAppSelector } from '../../../reducers';
import { useLocation } from 'react-router-dom';
import { getUtrFromSurvey } from '../../../selectors/survey';
import { getCurrentUser } from '../../../selectors/user';
import { useQuestionForm } from './QuestionReducer';
import SdgIcons from './toolbar/SdgIcons';
import { generateDisabledUtrs, hasUTRVFilter } from '@utils/survey/getDisableUtrs'
import { BlueprintContributions, ScopeQuestionGroup } from '@g17eco/types/survey';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import queryString from 'query-string';
import { ConnectionsContainer } from './connection/ConnectionsContainer';
import { useQuestionIds } from '@hooks/useQuestionIds';
import { useBookmarks } from '@features/survey-question-list/hooks/useBookmarks';
import { Document } from 'flexsearch';

interface QuestionContainerProps {
  surveyId: string;
  questionId: string;
  questionIndex: string;
  surveyGroups: ScopeQuestionGroup[];
  survey: SurveyActionData;
  blueprint: BlueprintContributions;
  searchIndex?: Document<unknown, false>;
}

export function QuestionContainerFooterStateless(props: QuestionContainerProps) {
  const {
    surveyId,
    questionId,
    questionIndex,
    surveyGroups,
    survey,
    blueprint,
    searchIndex,
  } = props;

  // @TODO - Remove these hooks
  const location = useLocation();

  const user = useAppSelector(getCurrentUser);
  const { bookmarks } = useBookmarks(surveyId);
  const { utr, utrv } = getUtrFromSurvey({ survey, surveyGroups, questionId });

  const disableUtrs = useMemo(() => {
    const query = queryString.parse(location.search, { arrayFormat: 'bracket' });
    if (!hasUTRVFilter(query)) {
      return [];
    }

    return generateDisabledUtrs({
      surveyGroups,
      blueprint,
      searchIndex,
      user,
      ...query,
      filterByBookmarks: query.filterByBookmarks === 'true',
      bookmarks,
    });
  }, [blueprint, bookmarks, location.search, searchIndex, surveyGroups, user]);

  const {
    altCode: alternativeCode,
  } = useQuestionIds(surveyGroups, questionIndex, questionId, disableUtrs)

  const { state } = useQuestionForm();
  const { assurancePortfolioLoaded, alternativeCodeOverride } = state;

  const isLoaded = user && blueprint && assurancePortfolioLoaded;
  const isUTRLoaded = utr && utrv;

  if (isLoaded && !isUTRLoaded) {
    return null;
  }

  const selectedAltCode = alternativeCodeOverride || alternativeCode || utr?.getType() || '';

  return (
    <>
      {user?.isStaff ? <ConnectionsContainer key={questionId} questionId={questionId} selectedStandard={selectedAltCode} /> : null}

      <SdgIcons additionalCodes={utr?.getTags('sdg') ?? []} blueprintInfo={blueprint?.[utr?.getCode() ?? '']} />

      {selectedAltCode === 'gri' ?
        <div className='mt-4 p-4 text-ThemeTextPlaceholder'>
          <strong>Disclaimer</strong>
          <p>WWG  has undergone certification through the GRI Certified Software and Tools Program. This means that
            GRI confirms that the content from the GRI Standards is being used accurately in this software or digital tool.</p>

          <p>WWG cannot guarantee that the use of this software or tool will result in producing a sustainability report
            that can be classified as a GRI Standards report. The complete GRI Standards have to be considered in order
            to ensure that the report will qualify as a GRI Standards report.</p>
        </div>
        : <></>
      }
    </>
  );
}


